import React, {useContext, useEffect, useState} from "react";
import {ColumnMatcher, parseCSVFile} from "../CSVParse";
import {produce} from "immer";
import AppContext from "../../appContext";
import WarningPopup, {useWarningState} from "../../components/WarningPopup";
import {useValidation} from "../../validation";
import {showSuccessOrFailed} from "../../Snacks";
import Papa from "papaparse";
import {saveAs} from "file-saver";
import {asOptionalRealNumber} from "../../controllers/helper";
import ManualFileUploader from "../../components/ManualFileUploader";
import FileUploader from "../../components/FileUploader";
import NumberNullable from "../../components/NumberNullable";
import CheckBox from "../../components/CheckBox";
import Input from "../../components/Input";
import SoilController from "../../controllers/SoilController";
import SoilValues from "../../controllers/SoilValues";
import ManualResult from "../../controllers/ManualResult";

type ValueKeys = keyof SoilValues;

interface ValueEntry {
    code: string,
    name: string,
    unit: string
}

// Ordered and labelled to match Column A of the Soil Results Mapping Reference Table (the import
// template, = Column A minus the export-only Latitude/Longitude/Sample depth). Units are baked into
// the `name` (single label) so the grid header and the CSV download both read exactly like the
// template. `code` is only used as a React key. keys (below) derives grid + paste column order.
const labEntryMappings: Record<ValueKeys, ValueEntry> = {
    caAndMgDivK:   { unit: '', name: '(Ca+Mg)/K', code: '(Ca+Mg)/K' },
    caPerc:        { unit: '', name: 'Ca%', code: 'Ca%' },
    caToMg:        { unit: '', name: 'Ca:Mg', code: 'Ca:Mg' },
    density:       { unit: '', name: 'Density (g/cm3)', code: 'Density (g/cm3)' },
    kPerc:         { unit: '', name: 'K%', code: 'K%' },
    mgPerc:        { unit: '', name: 'Mg%', code: 'Mg%' },
    mgToK:         { unit: '', name: 'Mg:K', code: 'Mg:K' },
    naPerc:        { unit: '', name: 'Na%', code: 'Na%' },
    naToK:         { unit: '', name: 'Na:K', code: 'Na:K' },
    pBrayI:        { unit: '', name: 'P Bray I', code: 'P Bray I' },
    pBrayII:       { unit: '', name: 'P Bray II', code: 'P Bray II' },
    pOlsen:        { unit: '', name: 'P Olsen', code: 'P Olsen' },
    pMehlichIII:   { unit: '', name: 'P Mehlich III', code: 'P Mehlich III' },
    sAmAC:         { unit: '', name: 'Sulphur', code: 'Sulphur' },
    phH2O:         { unit: '', name: 'pH (H2O)', code: 'pH (H2O)' },
    phKcl:         { unit: '', name: 'pH (KCl)', code: 'pH (KCl)' },
    k:             { unit: '', name: 'K (ppm)', code: 'K (ppm)' },
    ca:            { unit: '', name: 'Ca (ppm)', code: 'Ca (ppm)' },
    mg:            { unit: '', name: 'Mg (ppm)', code: 'Mg (ppm)' },
    na:            { unit: '', name: 'Na (ppm)', code: 'Na (ppm)' },
    fe:            { unit: '', name: 'Fe (ppm)', code: 'Fe (ppm)' },
    mn:            { unit: '', name: 'Mn (ppm)', code: 'Mn (ppm)' },
    zn:            { unit: '', name: 'Zn (ppm)', code: 'Zn (ppm)' },
    cu:            { unit: '', name: 'Cu (ppm)', code: 'Cu (ppm)' },
    exchAcidKcl:   { unit: '', name: 'Exch Acid KCL', code: 'Exch Acid KCL' },
    exchAcidK2SO4: { unit: '', name: 'Exch Acid K2SO4', code: 'Exch Acid K2SO4' },
    exchAcidM3:    { unit: '', name: 'Exch Acid M3', code: 'Exch Acid M3' },
    tValueAmAc:    { unit: '', name: 'CEC / T-value', code: 'CEC / T-value' },
    acidSatAmAc:   { unit: '', name: 'Acid saturation / H%', code: 'Acid saturation / H%' },
    sValue:        { unit: '', name: 'S-value', code: 'S-value' },
    bufferPh:      { unit: '', name: 'Buffer pH', code: 'Buffer pH' },
    organicMatter: { unit: '', name: 'Organic matter', code: 'Organic matter' },
    caCO3:         { unit: '', name: 'Calcium carbonate / CaCO3 (%)', code: 'Calcium carbonate / CaCO3 (%)' },
}

function matchArray(...array: string[]): (key: string) => boolean {
    return key => array.includes(key.toLowerCase().trim());
}

// Header-name based (column order irrelevant - see CSVParse). Each matcher accepts the Column-A
// template label plus legacy/lab aliases so both the new template and older files import cleanly.
const ManualSoilResultData: ColumnMatcher<ManualResult<SoilValues>>[] = [
    {
        match: matchArray('sample number', 'sample', 'samplenumber', 'reference'),
        populate: (value, entry) => entry.sampleNumber = value
    },
    {
        match: matchArray('lab number', 'lab', 'labnumber', 'lab no'),
        populate: (value, entry) => {
            entry.labNumber = value;
        }
    },
    {
        match: matchArray('ph kcl', 'ph (kcl)'),
        populate: (value, entry) => entry.values.phKcl = asOptionalRealNumber(value)
    },
    {
        match: matchArray('ph h2o', 'ph (h2o)'),
        populate: (value, entry) => entry.values.phH2O = asOptionalRealNumber(value)
    },
    {
        match: matchArray('p bray', 'pbray', 'p bray i'),
        populate: (value, entry) => entry.values.pBrayI = asOptionalRealNumber(value)
    },
    {
        match: matchArray('p bray 2', 'pbray2', 'p bray ii', 'pbrayii'),
        populate: (value, entry) => entry.values.pBrayII = asOptionalRealNumber(value)
    },
    {
        match: matchArray('p olsen', 'polsen'),
        populate: (value, entry) => entry.values.pOlsen = asOptionalRealNumber(value)
    },
    {
        match: matchArray('p mehlich iii', 'p mehlich 3', 'pmehlichiii', 'mehlich iii p'),
        populate: (value, entry) => entry.values.pMehlichIII = asOptionalRealNumber(value)
    },
    {
        match: matchArray('k (ppm)', 'k', 'k amac'),
        populate: (value, entry) => entry.values.k = asOptionalRealNumber(value)
    },
    {
        match: matchArray('na (ppm)', 'na', 'na amac'),
        populate: (value, entry) => entry.values.na = asOptionalRealNumber(value)
    },
    {
        match: matchArray('ca (ppm)', 'ca', 'ca amac'),
        populate: (value, entry) => entry.values.ca = asOptionalRealNumber(value)
    },
    {
        match: matchArray('mg (ppm)', 'mg', 'mg amac'),
        populate: (value, entry) => entry.values.mg = asOptionalRealNumber(value)
    },
    {
        match: matchArray('density', 'density (g/cm3)', 'dens.'),
        populate: (value, entry) => entry.values.density = asOptionalRealNumber(value)
    },
    {
        match: matchArray('sulphur', 's am ac', 'sam ac', 's amac'),
        populate: (value, entry) => entry.values.sAmAC = asOptionalRealNumber(value)
    },
    {
        match: matchArray('ca perc', 'ca%', 'ca% amac'),
        populate: (value, entry) => entry.values.caPerc = asOptionalRealNumber(value)
    },
    {
        match: matchArray('mg perc', 'mg%', 'mg% amac'),
        populate: (value, entry) => entry.values.mgPerc = asOptionalRealNumber(value)
    },
    {
        match: matchArray('k perc', 'k%', 'k% amac'),
        populate: (value, entry) => entry.values.kPerc = asOptionalRealNumber(value)
    },
    {
        match: matchArray('na perc', 'na%', 'na% amac'),
        populate: (value, entry) => entry.values.naPerc = asOptionalRealNumber(value)
    },
    {
        match: matchArray('ca to mg', 'ca:mg', 'ca:mg amac'),
        populate: (value, entry) => entry.values.caToMg = asOptionalRealNumber(value)
    },
    {
        match: matchArray('ca and mg div k', 'ca and mg / k', '(ca+mg)/k amac', '(ca+mg) / k', '(ca+mg)/k'),
        populate: (value, entry) => entry.values.caAndMgDivK = asOptionalRealNumber(value)
    },
    {
        match: matchArray('mg to k', 'mg:k', 'mg:k amac'),
        populate: (value, entry) => entry.values.mgToK = asOptionalRealNumber(value)
    },
    {
        match: matchArray('zn dtpa', 'zn', 'zn (ppm)'),
        populate: (value, entry) => entry.values.zn = asOptionalRealNumber(value)
    },
    {
        match: matchArray('cu dtpa', 'cu', 'cu (ppm)'),
        populate: (value, entry) => entry.values.cu = asOptionalRealNumber(value)
    },
    {
        match: matchArray('mn dtpa', 'mn', 'mn (ppm)'),
        populate: (value, entry) => entry.values.mn = asOptionalRealNumber(value)
    },
    {
        match: matchArray('fe dtpa', 'fe', 'fe (ppm)'),
        populate: (value, entry) => entry.values.fe = asOptionalRealNumber(value)
    },
    {
        match: matchArray('na to k', 'na:k', 'na:k amac'),
        populate: (value, entry) => entry.values.naToK = asOptionalRealNumber(value)
    },
    {
        match: matchArray('t-value amac', 'cec / t-value', 'cec/t-value', 'cec', 't-value'),
        populate: (value, entry) => entry.values.tValueAmAc = asOptionalRealNumber(value)
    },
    {
        match: matchArray('acid sat. amac', 'acid sat amac', 'acid saturation / h%', 'acid saturation', 'h%'),
        populate: (value, entry) => entry.values.acidSatAmAc = asOptionalRealNumber(value)
    },
    {
        match: matchArray('exch acid kcl'),
        populate: (value, entry) => entry.values.exchAcidKcl = asOptionalRealNumber(value)
    },
    {
        match: matchArray('exch acid k2so4'),
        populate: (value, entry) => entry.values.exchAcidK2SO4 = asOptionalRealNumber(value)
    },
    {
        match: matchArray('exch acid m3'),
        populate: (value, entry) => entry.values.exchAcidM3 = asOptionalRealNumber(value)
    },
    {
        match: matchArray('s-value', 's value', 'svalue'),
        populate: (value, entry) => entry.values.sValue = asOptionalRealNumber(value)
    },
    {
        match: matchArray('buffer ph', 'bufferph'),
        populate: (value, entry) => entry.values.bufferPh = asOptionalRealNumber(value)
    },
    {
        match: matchArray('organic matter', 'organic matter %', 'om'),
        populate: (value, entry) => entry.values.organicMatter = asOptionalRealNumber(value)
    },
    {
        match: matchArray('calcium carbonate / caco3 (%)', 'caco3', 'calcium carbonate', 'caco3 (%)'),
        populate: (value, entry) => entry.values.caCO3 = asOptionalRealNumber(value)
    },
]

const keys: (ValueKeys)[] = Object.keys(labEntryMappings) as unknown as (ValueKeys)[];

function BuildManualSoilResultString(sampleNumber: string): ManualResult<SoilValues> {
    return {
        sampleNumber,
        na: false,
        labNumber: '',
        values: {
            mn: null,
            na: null,
            phKcl: null,
            phH2O: null,
            pBrayI: null,
            pBrayII: null,
            pOlsen: null,
            pMehlichIII: null,
            k: null,
            ca: null,
            mg: null,
            density: null,
            sAmAC: null,
            caPerc: null,
            mgPerc: null,
            kPerc: null,
            naPerc: null,
            caToMg: null,
            caAndMgDivK: null,
            mgToK: null,
            naToK: null,
            zn: null,
            cu: null,
            fe: null,
            tValueAmAc: null,
            acidSatAmAc: null,
            exchAcidKcl: null,
            exchAcidK2SO4: null,
            exchAcidM3: null,
            sValue: null,
            bufferPh: null,
            organicMatter: null,
            caCO3: null
        }
    }
}

function interfaceSome<T extends object>(type: T, predicate: (value: T[keyof T]) => boolean): boolean {
    return Object.values(type).some(predicate);
}

const SoilManualLabResult: React.FC<{
    submissionId: number;
    onClose: () => void;
}> = (props) => {
    const context = useContext(AppContext);
    const warningState = useWarningState(null);
    const [rows, setRows] = useState<ManualResult<SoilValues>[]>([]);
    const [showUpload, setShowUpload] = useState(false);
    const [rowsNotUploaded, setRowsNotUploaded] = useState(0);

    useEffect(() => {
        // get initial sample number 
        // populate table
        SoilController.getSampleNumbersForSubmission(props.submissionId)
            .then(resp => {
                setRows(resp.data.map(sampleNumber => BuildManualSoilResultString(sampleNumber)))
            })
            .catch(err => context.showSnack(<>Could not get samples.</>))
    }, [])

    const validation = useValidation({
        everyRow: () => rows.every(row => {
            return row.na
                ? true
                : interfaceSome(row.values, f => f != null)
                    ? true
                    : row.labNumber.length > 0
        })
    })

    function save() {
        if (!validation.validate()) return

        const call = SoilController.updateManualSubmission({
            submissionId: props.submissionId,
            manualResult: rows
        })
        showSuccessOrFailed(context, call)
            .then(() => {
                setRowsNotUploaded(0)
                props.onClose()
            })
            .catch(e =>
                context.showSnack(<>Could not save result.</>)
            )
    }

    function downloadCSV() {
        const flat = rows.map(row => {
            const record: Record<string, string | number> = {
                "Sample Number": row.sampleNumber,
                "Lab Number": row.labNumber,
            };
            // Blank (not 0) for empty cells, keyed by the Column-A label. keys drives column order.
            keys.forEach(key => {
                record[labEntryMappings[key].name] = row.values[key] ?? '';
            });
            return record;
        })
        const csvData = Papa.unparse(flat);
        const blob = new Blob([csvData], {type: 'text/csv'});


        saveAs(blob, 'data.csv');
    }

    function BuildManualSoilResult(index: number): ManualResult<SoilValues> {
        return {
            sampleNumber: rows[index]?.sampleNumber ?? '',
            na: false,
            labNumber: '',
            values: {
                phKcl: null,
                phH2O: null,
                pBrayI: null,
                pBrayII: null,
                pOlsen: null,
                pMehlichIII: null,
                k: null,
                na: null,
                ca: null,
                mg: null,
                density: null,
                sAmAC: null,
                caPerc: null,
                mgPerc: null,
                kPerc: null,
                naPerc: null,
                caToMg: null,
                caAndMgDivK: null,
                mgToK: null,
                naToK: null,
                zn: null,
                mn: null,
                cu: null,
                fe: null,
                tValueAmAc: null,
                acidSatAmAc: null,
                exchAcidKcl: null,
                exchAcidK2SO4: null,
                exchAcidM3: null,
                sValue: null,
                bufferPh: null,
                organicMatter: null,
                caCO3: null,
            }
        }
    }

    function showWarningIfValidated() {
        if (!validation.validate()) return

        warningState.show("Are you sure you want " +
            " submit this manual submission?", null)
    }

    function csvUpload(file: File) {
        const reader = new FileReader()
        reader.onload = e => {
            const text: string = e.target?.result as string
            magicPaste(text);
        }
        reader.readAsText(file)
    }

    function magicPaste(text: string) {
        const entries = parseCSVFile<ManualResult<SoilValues>>(text, index => BuildManualSoilResult(index), ManualSoilResultData)
        let rowsNotFound = 0;
        const updatedRows = rows.map((row, index) => {
            const ent = entries.find(e => e.sampleNumber.toLowerCase().trim() == row.sampleNumber.toLowerCase().trim())
            if (ent) {
                return {...ent, sampleNumber: row.sampleNumber}
            }
            rowsNotFound++;
            return row
        })
        setRows(updatedRows)
        setRowsNotUploaded(rowsNotFound)
    }

    // from excel rows by \n, columns by \t
    function formatClipboard(clipboard: string): string[][] {
        return clipboard.split('\n').map(r => r.split('\t'))
    }

    function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
        const text = event.clipboardData.getData('text');
        if (!text || text === '') {
            return;
        }

        const data = formatClipboard(text);

        setRows(currentRows => currentRows.map((row, index) => {
            const rowData = data[index];
            if (rowData) {
                // Positional paste, but the positions derive from `keys` (Column-A / grid order):
                // column 0 is Lab number, then one analysis per column in grid order. Deriving from
                // keys means adding a field to labEntryMappings automatically extends paste too,
                // instead of the old hard-coded rowData[1..26] that silently dropped new columns.
                const values = {...row.values};
                keys.forEach((key, i) => {
                    values[key] = asOptionalRealNumber(rowData[i + 1] ?? "");
                });
                return {
                    ...row,
                    labNumber: rowData[0] ? rowData[0] : row.labNumber,
                    values,
                } as ManualResult<SoilValues>;
            }
            return row;
        }));
        event.preventDefault();
    }

    function uploadFiles() {
        setShowUpload(true)
    }

    return <div className={""}>
        <div className="overflow-x-auto pb-2">
            <table className="m-2o">
                <thead>
                <tr>
                    <td className="pr-2">Sample Number</td>
                    <td className="pr-2">Lab Number</td>
                    {keys.map(key =>
                        <td key={labEntryMappings[key].name + labEntryMappings[key].unit + labEntryMappings[key].code}
                            className="pr-2 text-xs whitespace-nowrap">
                            <div>
                                {labEntryMappings[key].name}
                            </div>
                            <div>
                                {labEntryMappings[key].unit}
                            </div>
                        </td>)}
                    <td className="pr-2">N/A</td>
                </tr>
                </thead>

                <tbody>

                {rows.map((result, index) =>
                    <tr key={result.sampleNumber + 'input'} className="text-gray-500">
                        <td className="pr-2 w-[10%] whitespace-nowrap">{result.sampleNumber}</td>
                        <td className="pr-2 w-[10%] ">
                            <Input onPaste={handlePaste}
                                   disabled={rows[index]!.na}
                                   className="w-full" value={result.labNumber}
                                   change={e => {
                                       setRows(produce(rows, draft => {
                                           draft[index]!.labNumber = e;
                                       }));
                                   }}/>
                        </td>

                        {keys.map(key => <td key={result.sampleNumber + 'number' + key} className="pr-2">
                            <NumberNullable value={result.values[key]}
                                            className={"min-w-[40px]"}
                                            disabled={rows[index]!.na}
                                            onPaste={handlePaste}
                                            change={val => {
                                                setRows(produce(rows, draft => {
                                                    draft[index]!.values[key] = val;
                                                }));

                                            }}/>
                        </td>)}
                        <td>
                            <CheckBox checked={rows[index]!.na} onChange={val => setRows(produce(rows, draft => {
                                draft[index]!.na = val
                            }))}/>
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>

        <div className="flex-1 flex justify-between items-center ">
            <div className='flex items-end'>
                <div className='btn bg-primary-500 mx-2' onClick={() => downloadCSV()}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                    </svg>
                </div>

                <div className='btn bg-primary-500 mx-2'>
                    <FileUploader onChange={file => csvUpload(file)} fileTypes=".csv">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5"
                             stroke="currentColor" className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
                        </svg>
                    </FileUploader>
                </div>

                <div className='btn bg-primary-500 mx-2' onClick={e => {
                    // get clipboard data
                    navigator.clipboard.readText().then(text => {
                        magicPaste(text);
                    })
                }}>
                    <div className="flex">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6">
                            <path
                                d="M5 5h2v1c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V5h2v5h2V5c0-1.1-.9-2-2-2h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h5v-2H5V5zm7-2c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1z"
                                fill="currentColor"></path>
                            <path
                                d="M22.3 20.9l-2-2c.58-1.01.95-2.23.51-3.65c-.53-1.72-2.04-3.05-3.84-3.22a4.498 4.498 0 0 0-4.95 4.95c.18 1.79 1.5 3.31 3.22 3.84c1.43.44 2.64.07 3.65-.51l2 2c.39.39 1.01.39 1.4 0s.4-1.02.01-1.41zM16.5 19c-1.4 0-2.5-1.1-2.5-2.5s1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5s-1.1 2.5-2.5 2.5z"
                                fill="currentColor"></path>
                        </svg>
                    </div>
                </div>

                <div className='btn bg-primary-500 mx-2' onClick={() => uploadFiles()}>
                    Upload files
                </div>


                {
                    rowsNotUploaded > 0
                        ? <div className='text-sm text-red-500'>
                            ({rowsNotUploaded} rows not uploaded)
                        </div>
                        : null
                }
            </div>

            <div className='flex justify-between sm:justify-end items-center'>
                {
                    !validation.rules.everyRow
                        ? <div className="text-xs text-red-500 pr-2">(Please ensure that each row contains at least one
                            entry or is marked as N/A)</div>
                        : null
                }
                <div className="btn btn-primary my-2" onClick={() => showWarningIfValidated()}>Save</div>
            </div>
        </div>

        <WarningPopup state={warningState} onYes={save}/>

        <ManualFileUploader show={showUpload}
                            setShow={setShowUpload}
                            submissionId={props.submissionId}/>
    </div>
}

export default SoilManualLabResult;

